
import { Injectable, OnDestroy } from '@angular/core';
import { Attachment } from '@docma-mm-core/attachment/attachment';
import { AttachmentService } from '@docma-mm-core/attachment/attachment.service';
import { CapacitorAppSettingsService } from '../../../capacitor-app-settings/Services/capacitor-app-settings.service';
import { MmTranslateService } from '../../../mm-translate.service';
import { NotificationService } from '@docma-core/notification/notification.service';
import { PlatformModeStateService } from '../../../mm-offline/services/platform-mode-state.service';
import { Task, TaskScopeEnum, TaskService, TaskStatus, TaskStatusEnum, TaskStatusDataTypeEnum } from '@mydocma/task';
import { AttachmentDownloadJob } from './attachment-download.job';
import { BehaviorSubject, filter, firstValueFrom, interval, map, Observable, Subject } from 'rxjs';
import {AttachmentDbService} from '@docma-mm-core/attachment/attachment-db.service';
import { LoadStatusEnum } from '@docma-mm-core/model/load-state/load-state.enum';
import { AppContainerOptions, AppRightDrawerService } from '@mydocma/app-container';


@Injectable({
  providedIn: 'root'
})
export class AttachmentDownloadService implements OnDestroy {

  private jobs: Array<AttachmentDownloadJob> = [];

  private onAttachmentStatusChangedSubject = new BehaviorSubject<{attachment: Attachment; faultUid: string; status: LoadStatusEnum;} | null>(null);
  onAttachmentStatusChanged = this.onAttachmentStatusChangedSubject.asObservable();

  constructor(
    private readonly attachmentService: AttachmentService,
    private readonly capacitorAppSettingsService: CapacitorAppSettingsService,
    private readonly platformModeStateService: PlatformModeStateService,
    private readonly taskService: TaskService,
    private readonly translateService: MmTranslateService,
    private readonly attachmentDbService: AttachmentDbService,
    private readonly appRightDrawerService: AppRightDrawerService,
    private readonly appContainerOptions: AppContainerOptions,) {

    // allow only one concurrent job
    interval(1000).subscribe(() => {
      for (let job of this.jobs) {
        const taskStatus = job.task.status?.value
        if (taskStatus === TaskStatusEnum.started) {
          break;
        }
        else if (taskStatus === TaskStatusEnum.init) {
          this.executeDownloadJob(job);
          break;
        }
      }
    });
  }

  ngOnDestroy(): void { }

  private async executeDownloadJob(job: AttachmentDownloadJob) {

    job.task.status = new TaskStatus({ value: TaskStatusEnum.started });
    job.failedFaultUIds.clear();
    this.taskService.updateTask(job.task).subscribe();

    const attachments = Array.from(job.attachments);
    let index = 0;

    if(attachments && attachments.length > 0 && job.openMessageCenter) {
      this.appRightDrawerService.openContent('task-dashboard', this.appContainerOptions);
    }
    

    for (let next of attachments) {
      const [faultUId, entry] = next;
      const { attachments: faultAttachments, mydocmaUid } = entry;
      const requests = [];

      for (let a of (faultAttachments || [])) {
        this.onAttachmentStatusChangedSubject.next({attachment: a.attachment, faultUid: faultUId, status: LoadStatusEnum.Loading});
      }
      
    }

    for (let next of attachments) {
      index++;
      const [faultUId, entry] = next;
      const { attachments: faultAttachments, mydocmaUid } = entry;
      const requests = [];

      for (let a of (faultAttachments || [])) {
        requests.push(
          this.downloadAttachment(faultUId, a.attachment as any, mydocmaUid, a.activityUid, job.onTaskCanceled)
        );
      }

      const results = await Promise.allSettled(requests);
      const hasFailed = results.some(r => r.status === 'rejected');
      if (hasFailed) {
        job.markFaultAsFailed(faultUId);
      }

      job.task.percent = ((index / job.totalCount) * 100);
    }
    if (job.failedCount > 0) {
      const message = await firstValueFrom(
        this.translateService.get('MM.attachment.errors.downloadPartialFailed', {
          failed: job.failedCount,
          total: job.totalCount
        })
      );
      job.task.status = new TaskStatus({ value: TaskStatusEnum.info });
      job.task.status.data = {
        type: TaskStatusDataTypeEnum.Error,
        text: message
      };
      this.taskService.updateTask(job.task).subscribe();

      if (job.failedCount < job.totalCount) {
        job.task.status = new TaskStatus({ value: TaskStatusEnum.ended });
      } else {
        job.task.status = new TaskStatus({ value: TaskStatusEnum.failed });
      }

    } else {
      job.task.status = new TaskStatus({ value: TaskStatusEnum.ended });
    }
    this.taskService.updateTask(job.task).subscribe();
  }

  public async startBackgroundDownload(job: AttachmentDownloadJob): Promise<Task> {

    const name = await firstValueFrom(
      this.translateService.get('MM.attachment.backgroundDownload')
    );

    let task = new Task({
      name: name,
      cancelable: true,
      scope: [TaskScopeEnum.local]
    });
    task = await firstValueFrom(this.taskService.createTask(task));
    job.task = task;
    const cancel$ = new BehaviorSubject<boolean>(false);
    job.onTaskCanceled = cancel$.asObservable();

    this.taskService.onTaskChanged.pipe(
      filter(t => t.id === task.id && t.status?.value === TaskStatusEnum.canceled)
    ).subscribe(() => cancel$.next(true));
    this.jobs.push(job);
    return task;
  }

  public async downloadAttachment(faultUid:string, a: Attachment, mydocmaUid: string, acitivityUid?: string, cancelation?: Observable<any>): Promise<Attachment> {
    const downloadSize = this.capacitorAppSettingsService.currentImageDownloadSize;
    if (this.platformModeStateService.useNewAttachmentDownload) {
      await this.attachmentService.downloadAttachmentSource(a, a.source, true, downloadSize, cancelation);
    } else {
      const alreadyLoaded =
        a.source?.loadState?.status === LoadStatusEnum.Loaded &&
        a.source?.loadState?.data instanceof File &&
        (a.source.loadState.data as File).size > 0;
      if (!alreadyLoaded) {
        await this.attachmentService.loadAttachmentSource(a, a.source, true, downloadSize, cancelation);
      }
    }
    await this.attachmentDbService.saveLoadStateStatus(mydocmaUid, faultUid, a.uid, acitivityUid, a.source.loadState.status);
    this.onAttachmentStatusChangedSubject.next({attachment: a, faultUid, status: a.source.loadState.status});
    return a;
  }

}
